import type { Head } from '@unhead/vue'
import type { TranslationsMap } from '@/models/translations'
import { getMediaById, normalizeMedia, type AttachmentModel } from '@/utils/media'

type RecordLike = Record<string, unknown>

export type SeoSource = {
  id?: number
  title?: string
  type?: string
  relativeUrl?: string
  media?: number
  meta?: unknown
  seo?: unknown
  hero?: unknown
  translations?: TranslationsMap
}

export type BuildSeoHeadOptions = {
  routePath: string
  locale?: string
  siteName: string
  siteOrigin?: string
  includeCanonical?: boolean
  content?: SeoSource | null
  attachments?: AttachmentModel[]
  defaults: {
    title: string
    description: string
    image?: string
  }
}
type SeoOgType =
  | 'article'
  | 'website'
  | 'book'
  | 'profile'
  | 'music.song'
  | 'music.album'
  | 'music.playlist'
  | 'music.radio_status'
  | 'video.movie'
  | 'video.episode'
  | 'video.tv_show'
  | 'video.other'

function asRecord(value: unknown): RecordLike | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as RecordLike)
    : null
}

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

function withMaxImagePreview(value?: string) {
  const maxImagePreview = 'max-image-preview:large'
  const directives = (value ?? '')
    .split(',')
    .map((directive) => directive.trim())
    .filter(Boolean)

  if (!directives.some((directive) => directive.toLowerCase() === maxImagePreview)) {
    directives.push(maxImagePreview)
  }

  return directives.join(', ')
}

function normalizeText(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const text = stripHtml(value)
    return text || undefined
  }

  if (typeof value === 'number') {
    return String(value)
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const text = normalizeText(item)
      if (text) return text
    }
    return undefined
  }

  const record = asRecord(value)
  if (!record) return undefined

  return firstString(record, [
    ['rendered'],
    ['description'],
    ['text'],
    ['content'],
    ['title'],
    ['value'],
  ])
}

function getByPath(source: RecordLike, path: string[]) {
  let current: unknown = source

  for (const segment of path) {
    const record = asRecord(current)
    if (!record || !(segment in record)) return undefined
    current = record[segment]
  }

  return current
}

function firstValue(source: RecordLike | null, paths: string[][]) {
  if (!source) return undefined

  for (const path of paths) {
    const value = getByPath(source, path)
    if (value != null && value !== '') return value
  }

  return undefined
}

function firstString(source: RecordLike | null, paths: string[][]) {
  const value = firstValue(source, paths)
  return normalizeText(value)
}

function normalizeHtmlLang(locale?: string) {
  return locale?.toLowerCase().startsWith('en') ? 'en' : 'no'
}

function normalizeOgLocale(locale?: string) {
  return normalizeHtmlLang(locale) === 'en' ? 'en_US' : 'nb_NO'
}

function normalizeOgType(value: string | undefined, fallback: SeoOgType): SeoOgType {
  switch (value) {
    case 'article':
    case 'website':
    case 'book':
    case 'profile':
    case 'music.song':
    case 'music.album':
    case 'music.playlist':
    case 'music.radio_status':
    case 'video.movie':
    case 'video.episode':
    case 'video.tv_show':
    case 'video.other':
      return value
    default:
      return fallback
  }
}

function trimTrailingSlash(url: string) {
  if (url.length <= 1) return url
  return url.endsWith('/') ? url.slice(0, -1) : url
}

function buildCanonicalUrl(candidate: string | undefined, routePath: string, siteOrigin?: string) {
  const next = candidate || routePath || '/'

  try {
    const url = siteOrigin
      ? new URL(next, siteOrigin)
      : new URL(next)

    url.hash = ''
    url.search = ''

    if (url.pathname !== '/') {
      url.pathname = trimTrailingSlash(url.pathname)
    }

    return trimTrailingSlash(url.toString())
  } catch {
    const clean = next.replace(/[?#].*$/, '')
    if (clean === '/') return clean
    return trimTrailingSlash(clean)
  }
}

function imageUrlFromUnknown(
  value: unknown,
  attachments: AttachmentModel[],
): string | undefined {
  if (typeof value === 'string') {
    const text = value.trim()
    return text || undefined
  }

  if (typeof value === 'number') {
    const media = normalizeMedia(getMediaById(attachments, value))
    return media?.type === 'image' ? media.url : undefined
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const image = imageUrlFromUnknown(item, attachments)
      if (image) return image
    }
    return undefined
  }

  const record = asRecord(value)
  if (!record) return undefined

  const direct = firstString(record, [
    ['url'],
    ['src'],
    ['source_url'],
    ['image_url'],
    ['og_image'],
    ['guid', 'rendered'],
    ['sizes', 'full', 'source_url'],
    ['sizes', 'large', 'source_url'],
    ['image', 'url'],
    ['image', 'source_url'],
    ['image', 'sizes', 'full', 'source_url'],
  ])

  if (direct) return direct

  const maybeId = firstValue(record, [
    ['id'],
    ['image', 'id'],
    ['attachment_id'],
    ['media_id'],
  ])

  if (typeof maybeId === 'number') {
    const media = normalizeMedia(getMediaById(attachments, maybeId))
    return media?.type === 'image' ? media.url : undefined
  }

  return undefined
}

function resolveImage(
  meta: RecordLike | null,
  attachments: AttachmentModel[],
  fallbackMediaId?: number,
  paths: string[][] = [],
) {
  const image = imageUrlFromUnknown(firstValue(meta, paths), attachments)
  if (image) return image

  if (typeof fallbackMediaId === 'number') {
    const media = normalizeMedia(getMediaById(attachments, fallbackMediaId))
    if (media?.type === 'image') return media.url
  }

  return undefined
}

function resolvePublisherJsonLd(seo: RecordLike | null): Record<string, unknown> | undefined {
  const publisher = asRecord(firstValue(seo, [['publisher']]))
  if (!publisher) return undefined

  const normalized = pruneJsonLd(publisher)

  return asRecord(normalized) ?? undefined
}

function pruneJsonLd(value: unknown): unknown {
  if (Array.isArray(value)) {
    const items = value
      .map(pruneJsonLd)
      .filter((item) => item != null)

    return items.length ? items : undefined
  }

  const record = asRecord(value)
  if (!record) {
    if (typeof value === 'string') {
      const trimmed = value.trim()
      return trimmed ? trimmed : undefined
    }

    return value ?? undefined
  }

  const next: Record<string, unknown> = {}

  for (const [key, item] of Object.entries(record)) {
    const normalized = pruneJsonLd(item)
    if (normalized != null) {
      next[key] = normalized
    }
  }

  return Object.keys(next).length ? next : undefined
}

function hydrateJsonLdNode(
  value: unknown,
  fallback: {
    canonical: string
    description: string
    title: string
  },
): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => hydrateJsonLdNode(item, fallback))
  }

  const record = asRecord(value)
  if (!record) return value

  const type = normalizeText(record['@type'])
  const next: Record<string, unknown> = { ...record }

  if (!normalizeText(next.name)) {
    next.name = fallback.title
  }

  if ((type === 'WebPage' || type === 'MedicalWebPage' || type === 'Article') && !normalizeText(next.description)) {
    next.description = fallback.description
  }

  if (type === 'WebPage' || type === 'MedicalWebPage' || type === 'Article') {
    if (!normalizeText(next.url)) {
      next.url = fallback.canonical
    }

    const mainEntityOfPage = asRecord(next.mainEntityOfPage)
    if (mainEntityOfPage && !normalizeText(mainEntityOfPage['@id'])) {
      next.mainEntityOfPage = {
        ...mainEntityOfPage,
        '@id': fallback.canonical,
      }
    }
  }

  return next
}

function resolveJsonLdFromSeo(
  seo: RecordLike | null,
  fallback: {
    canonical: string
    description: string
    title: string
  },
): unknown {
  const rawJsonLd = firstValue(seo, [
    ['jsonLd'],
    ['jsonld'],
    ['json_ld'],
  ])

  const hydrated = hydrateJsonLdNode(rawJsonLd, fallback)
  return pruneJsonLd(hydrated)
}

function buildPageJsonLd(options: {
  canonical: string
  description: string
  publisher?: Record<string, unknown>
  title: string
  type: SeoOgType
}) {
  return {
    '@context': 'https://schema.org',
    '@type': options.type === 'article' ? 'Article' : 'WebPage',
    '@id': `${options.canonical}#webpage`,
    url: options.canonical,
    name: options.title,
    headline: options.title,
    description: options.description,
    ...(options.publisher ? { publisher: options.publisher } : {}),
  }
}

function resolveAlternateLanguageLinks(content?: SeoSource | null) {
  const translations = content?.translations
  if (!translations) return []

  const seen = new Set<string>()
  const normalizedTranslations = Object.values(translations)

  const links = normalizedTranslations.flatMap((translation) => {
    const hreflang = translation.hreflang?.trim()
    const href = translation.link?.trim()

    if (!translation.exists || !hreflang || !href || seen.has(hreflang)) {
      return []
    }

    seen.add(hreflang)

    return [{
      key: `alternate-${hreflang}`,
      rel: 'alternate' as const,
      hreflang,
      href,
    }]
  })

  if (!seen.has('x-default')) {
    const defaultTranslation = normalizedTranslations.find((translation) => {
      const hreflang = translation.hreflang?.trim().toLowerCase()
      return translation.exists && (hreflang === 'no' || hreflang === 'nb' || hreflang === 'nb-no')
    })

    const defaultHref = defaultTranslation?.link?.trim()

    if (defaultHref) {
      links.push({
        key: 'alternate-x-default',
        rel: 'alternate' as const,
        hreflang: 'x-default',
        href: defaultHref,
      })
    }
  }

  return links
}

function resolveFallbackTitle(content?: SeoSource | null) {
  return firstString(asRecord(content?.hero), [
    ['title'],
  ])
}

export function resolveSeoTitle(content: SeoSource | null | undefined, fallback: string) {
  const seo = asRecord(content?.seo)

  return (
    firstString(seo, [
      ['title'],
    ]) ||
    resolveFallbackTitle(content) ||
    normalizeText(content?.title) ||
    fallback
  )
}

function resolveFallbackDescription(content?: SeoSource | null) {
  return (
    firstString(asRecord(content?.hero), [
      ['content_html'],
      ['content'],
    ])
  )
}

function resolveHeroImage(
  content: SeoSource | null | undefined,
  attachments: AttachmentModel[],
) {
  const hero = asRecord(content?.hero)

  if (!hero) return undefined

  return imageUrlFromUnknown(firstValue(hero, [
    ['media', 'image_url'],
    ['media', 'image_id'],
    ['media'],
  ]), attachments)
}

export function buildSeoHead(options: BuildSeoHeadOptions): Head {
  const {
    attachments = [],
    content,
    defaults,
    includeCanonical = true,
    locale,
    routePath,
    siteName,
    siteOrigin,
  } = options

  const htmlLang = normalizeHtmlLang(locale)
  const ogLocale = normalizeOgLocale(locale)
  const seo = asRecord(content?.seo)

  const title = resolveSeoTitle(content, defaults.title)

  const description =
    firstString(seo, [
      ['description'],
    ]) ||
    resolveFallbackDescription(content) ||
    defaults.description

  const canonical = buildCanonicalUrl(
    firstString(seo, [
      ['canonical'],
    ]) || content?.relativeUrl,
    routePath,
    siteOrigin,
  )

  const ogTitle =
    firstString(seo, [
      ['og', 'title'],
    ]) || title

  const ogDescription =
    firstString(seo, [
      ['og', 'description'],
    ]) || description

  const ogImage =
    resolveImage(seo, attachments, content?.media, [
      ['og', 'image'],
    ]) ||
    resolveHeroImage(content, attachments) ||
    defaults.image

  const ogUrl = buildCanonicalUrl(
    firstString(seo, [
      ['og', 'url'],
    ]) || canonical,
    routePath,
    siteOrigin,
  )

  const twitterTitle =
    firstString(seo, [
      ['twitter', 'title'],
    ]) || ogTitle

  const twitterDescription =
    firstString(seo, [
      ['twitter', 'description'],
    ]) || ogDescription

  const twitterImage =
    resolveImage(seo, attachments, content?.media, [
      ['twitter', 'image'],
    ]) || ogImage

  const twitterCard =
    firstString(seo, [
      ['twitter', 'card'],
    ]) || (twitterImage ? 'summary_large_image' : 'summary')

  const twitterSite = firstString(seo, [
    ['twitter', 'site'],
  ])

  const robots = firstString(seo, [
    ['robots'],
  ])

  const ogType = normalizeOgType(
    firstString(seo, [
      ['og', 'type'],
    ]),
    content?.type === 'post' ? 'article' : 'website',
  )

  const jsonLd = resolveJsonLdFromSeo(seo, {
    canonical,
    description,
    title,
  }) ?? buildPageJsonLd({
    canonical,
    description,
    publisher: resolvePublisherJsonLd(seo),
    title,
    type: ogType,
  })

  const resolvedSiteName =
    firstString(seo, [
      ['og', 'site_name'],
      ['twitter', 'site'],
    ]) || siteName

  const headMeta = [
    { name: 'description', content: description },
    { property: 'og:type', content: ogType },
    { property: 'og:url', content: ogUrl },
    { property: 'og:title', content: ogTitle },
    { property: 'og:description', content: ogDescription },
    { property: 'og:site_name', content: resolvedSiteName },
    { property: 'og:locale', content: ogLocale },
    { name: 'twitter:card', content: twitterCard },
    { name: 'twitter:url', content: canonical },
    { name: 'twitter:title', content: twitterTitle },
    { name: 'twitter:description', content: twitterDescription },
  ]

  if (ogImage) {
    headMeta.push({ property: 'og:image', content: ogImage })
  }

  if (twitterImage) {
    headMeta.push({ name: 'twitter:image', content: twitterImage })
  }

  if (twitterSite) {
    headMeta.push({ name: 'twitter:site', content: twitterSite })
  }

  headMeta.push({ name: 'robots', content: withMaxImagePreview(robots) })

  return {
    htmlAttrs: {
      lang: htmlLang,
    },
    title,
    link: [
      ...(includeCanonical
        ? [{
            key: 'canonical',
            rel: 'canonical' as const,
            href: canonical,
          }]
        : []),
      ...resolveAlternateLanguageLinks(content),
    ],
    meta: headMeta,
    script: jsonLd
      ? [
          {
            key: 'route-jsonld',
            id: 'route-jsonld',
            type: 'application/ld+json',
            textContent: JSON.stringify(jsonLd),
          },
        ]
      : [],
  }
}

export default buildSeoHead
